import { isPlatformApiError } from '@traceio-org/platform-client';
import { DEFAULT_CLAIM_NAMESPACE, DEFAULT_PLATFORM_API_AUDIENCE, hubUrl, internalAccessCheckResponseSchema, isOrganizationReference, parseReturnPath, readTraceClaims, takeSignInOrganization, } from '@traceio-org/platform-contracts';
import { hasAuthCallbackParams } from '../session/app-state.js';
import { AUTH_CALLBACK_PATH } from '../session/auth0-session.js';
import { decodeTokenPayload } from '../session/token-claims.js';
import { isContextNotCarriedError, isSessionExpiredError, isSignInError, isSignInRedirectError, SessionExpiredError, } from '../session/types.js';
import { clearSignInAttempt, defaultAttemptStorage, readSignInAttempt, writeSignInAttempt, } from './attempts.js';
import { defaultRememberedStorage, forgetRememberedSignIn, readRememberedSignIn, writeRememberedSignIn, } from './remembered.js';
import { throwIfAborted } from '../abort.js';
/** platform-api answered, but not with something this destination may act on. */
export class DestinationCheckError extends Error {
    name = 'DestinationCheckError';
    code;
    constructor(code, options) {
        super(`The access check of this destination failed (${code}).`, options);
        this.code = code;
    }
}
/**
 * Takes the organization the hub handed over (SIGN_IN_ORGANIZATION_PARAM) out of this page's address, before
 * anything else reads the address, and returns it: null when there was none, or not exactly one well-formed
 * one. A sign-in hint only: Auth0 signs in members of that organization only, and access is checked after.
 */
export function takeHandedOrganization() {
    const address = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const { organization, path } = takeSignInOrganization(address);
    if (path !== address)
        window.history.replaceState(window.history.state, '', path);
    return organization;
}
/**
 * The promise, or the signal's reason as soon as the signal aborts: a hook that ignores its signal (or never
 * answers) cannot hold the check past an abort.
 */
function untilAborted(promise, signal) {
    if (!signal)
        return promise;
    if (signal.aborted)
        return Promise.reject(signal.reason);
    return new Promise((resolve, reject) => {
        const onAbort = () => reject(signal.reason);
        signal.addEventListener('abort', onAbort, { once: true });
        promise.then((value) => {
            signal.removeEventListener('abort', onAbort);
            resolve(value);
        }, (error) => {
            signal.removeEventListener('abort', onAbort);
            reject(error);
        });
    });
}
function isAbortError(error) {
    return typeof error === 'object' && error !== null && error.name === 'AbortError';
}
function httpStatus(error) {
    return isPlatformApiError(error) && error.kind === 'http' ? error.status : null;
}
/**
 * The path, query and fragment of the current page when parseReturnPath accepts them, else null. Without any
 * SIGN_IN_ORGANIZATION_PARAM: the gate took the handed one out of the address already, so one there now was
 * written later, and a return path never carries it further (to the hub, or through a sign-in and back).
 */
function currentReturnPath() {
    const url = new URL(window.location.href);
    const { path } = takeSignInOrganization(`${url.pathname}${url.search}${url.hash}`);
    const result = parseReturnPath(path);
    return result.ok ? result.path : null;
}
/** True when the URL's origin is exactly this page's origin (scheme, host and port). */
function isThisOrigin(rawUrl) {
    try {
        return new URL(rawUrl).origin === window.location.origin;
    }
    catch {
        return false;
    }
}
function personOf(me) {
    const person = { userId: me.subject.userId, displayName: me.subject.displayName };
    if (me.subject.email)
        person.email = me.subject.email;
    return person;
}
function personOfUser(user) {
    const person = { userId: user.id, displayName: user.displayName };
    if (user.email)
        person.email = user.email;
    return person;
}
/** The HTTP status a failed hook or client call carries, if any (PlatformApiError and plain errors alike). */
function statusOf(error) {
    if (typeof error !== 'object' || error === null)
        return null;
    const status = error.status;
    return typeof status === 'number' ? status : null;
}
/**
 * Whether an allowed answer's context is the one the product token names. The token's Trace claims are decoded
 * and never trusted: the backend verified the token, and this only catches a backend that answers for another
 * workspace than the token it was given names. A token that is not a JWT, or names no context, has nothing to
 * disagree with; malformed Trace claims disagree with every context.
 */
function tokenNamesContext(token, context, namespace) {
    const payload = decodeTokenPayload(token);
    if (!payload)
        return true;
    const claims = readTraceClaims(payload, namespace);
    if (!claims.success)
        return false;
    const { organizationId, portalId, customerAccountId } = claims.data;
    if (context.type === 'employee') {
        return (portalId === undefined && (organizationId === undefined || organizationId === context.organization.id));
    }
    return ((organizationId === undefined || organizationId === context.providerOrganization.id) &&
        (portalId === undefined ||
            (portalId === context.portal.id && customerAccountId === context.customerAccount.id)));
}
/** A product context in which the person could open the product now or after a sign-in (sso_required). */
function offersProduct(context, productKey) {
    return context.products.some((p) => p.productKey === productKey && (p.state === 'available' || p.state === 'sso_required'));
}
/**
 * Why development identity refused an organization-scoped token for a chosen workspace: 403 sso_required
 * (the organization enforces another sign-in) or 403 forbidden (the person does not hold it here). Anything
 * else is not a scope refusal.
 */
function scopeRefusalOf(error) {
    if (!isPlatformApiError(error) || error.kind !== 'http' || error.status !== 403)
        return null;
    if (error.code === 'sso_required')
        return 'sso_required';
    return error.code === 'forbidden' ? 'context_not_found' : null;
}
/**
 * The access check every destination runs before it renders anything (DestinationGate wraps it). It never
 * grants access by default: every path ends in exactly one outcome, and only a platform-api answer that
 * passes every rule below is "granted".
 *
 * a. On /auth/callback with an authorization response (Auth0), it completes the sign-in, restores the
 *    validated return path in place of the callback URL and forgets the sign-in attempt.
 * b. It gets a token for this application's own audience. Without a session: the first time it sends the
 *    person to the hub sign-in (leaveForHub records that in this tab); back from the hub within five
 *    minutes, Auth0 signs the person in silently through its SSO session; still nothing after that is a
 *    recoverable error, never another round (the loop guard). Development identity uses the shared local
 *    session the hub started, so coming back without one is already the error.
 * c. It checks authorization: Trace Admin with GET /v1/admin/me and the admin-audience token; a product
 *    with a backend through options.checkAccess and its product token only (the backend asks platform-api
 *    POST /v1/internal/access/check), where only an allowed answer for this product, in the workspace the
 *    person chose if any and the one the token's Trace claims name if any, where the product is available,
 *    counts; every other product with POST /v1/navigation/decide and a platform-audience token, where only
 *    a redirect for this product to this page's own origin counts, and me/access supplies the context. Both
 *    product paths route denials to the same hub pages and share the loop guards.
 *
 * Tokens stay in the sessions' memory and in Authorization headers: never in a URL, the marker or
 * localStorage. Every failure is an outcome, except an abandoned attempt: once options.signal aborted (the
 * gate's time limit or unmount), the check rejects with the abort reason and leaves nothing behind for the next
 * attempt. The session calls cannot all be cancelled, so after every await the check looks at the signal before
 * it writes or clears the marker, replaces the address, starts a sign-in or asks for a workspace's token (an
 * Auth0 session would leave for it), and the signal goes to the session calls that can start a sign-in.
 */
export async function ensureDestinationAccess(options) {
    // An attempt abandoned before it starts does nothing, not even take the handed organization out of the address.
    throwIfAborted(options.signal);
    const outcome = await checkDestination(options);
    // Whatever its last step answered, an attempt abandoned while it ran has no outcome to act on.
    throwIfAborted(options.signal);
    return outcome;
}
/** The check of ensureDestinationAccess, inside the abort checks around it. */
async function checkDestination(options) {
    const { productKey, session, client, hubOrigin, audience, signal, checkAccess } = options;
    // Out of the address first, so no return path, router or sign-in ever carries it further.
    const handedOrganization = options.signInOrganization === undefined ? takeHandedOrganization() : options.signInOrganization;
    const platformAudience = options.platformAudience ?? DEFAULT_PLATFORM_API_AUDIENCE;
    const claimNamespace = options.claimNamespace ?? DEFAULT_CLAIM_NAMESPACE;
    const storage = options.storage === undefined ? defaultAttemptStorage() : options.storage;
    const remembered = options.rememberedStorage === undefined ? defaultRememberedStorage() : options.rememberedStorage;
    const now = options.now ?? Date.now;
    // Risky by nature, so spelled out: the gate abandons an attempt by aborting its signal, and the next attempt
    // must find the marker, the address and the session exactly as this one found them. live() ends an abandoned
    // attempt with the abort reason; it runs after every await and before every such side effect.
    const live = () => throwIfAborted(signal);
    const withSignal = signal ? { signal } : {};
    /** A token for this audience (for a chosen workspace when scope is given), with the attempt's signal. */
    const tokenFor = (wanted, scope) => scope === undefined && signal === undefined
        ? session.getAccessToken(wanted)
        : session.getAccessToken(wanted, { ...scope, ...withSignal });
    const signIn = (returnPath) => hubUrl(hubOrigin, { route: 'login', productKey, ...(returnPath ? { returnPath } : {}) });
    const failed = (code, returnPath) => ({
        kind: 'sign-in-failed',
        code,
        hubUrl: signIn(returnPath),
    });
    // Trace Admin's backend is platform-api's own staff routes; a product backend hook cannot stand in for it.
    if (checkAccess && productKey === 'admin') {
        return { kind: 'error', error: new DestinationCheckError('check_access_unsupported') };
    }
    // With a product backend, the product token names the workspace the person chose (organization-scoped):
    // development identity mints it for the context, Auth0 signs in to its organization (Auth0Session).
    const productToken = (contextId, organization) => tokenFor(audience, contextId === undefined ? undefined : { contextId, ...(organization ? { organization } : {}) });
    try {
        // a. Finish an Auth0 redirect.
        let completedSignIn = false;
        let answered;
        // The workspace this page is for: chosen here, or carried through the sign-in that just completed.
        let chosenId = options.contextId;
        let chosenOrganization = options.contextOrganization;
        if (session.mode === 'auth0' &&
            window.location.pathname === AUTH_CALLBACK_PATH &&
            hasAuthCallbackParams(window.location.search)) {
            let appState;
            // Read before it is cleared: a silent sign-in (stage silent) that comes back refused goes to the hub.
            const before = storage ? readSignInAttempt(storage, now()) : null;
            try {
                // The completion itself goes on when the attempt is abandoned: it is shared by every attempt of this
                // page (the SDK consumes the sign-in transaction once), so the next attempt still gets its answer.
                appState = await untilAborted(session.completeSignIn(window.location.href), signal);
            }
            catch (error) {
                live();
                if (storage)
                    clearSignInAttempt(storage);
                if (isSignInError(error) && error.appState.silent && before?.stage === 'silent') {
                    // The direct sign-in with the remembered workspace was refused (no SSO session, MFA due, no longer a
                    // member, ...): the usual trip through the hub, exactly as without a remembered sign-in. The trip
                    // writes the hub stage, so this never repeats. A refusal other than an ended session forgets it.
                    if (remembered && error.providerError !== 'login_required')
                        forgetRememberedSignIn(remembered);
                    return { kind: 'sign-in', hubUrl: signIn(error.appState.returnPath ?? null) };
                }
                return failed(isSignInError(error) ? error.code : 'unknown', isSignInError(error) ? error.appState.returnPath : null);
            }
            live();
            if (storage)
                clearSignInAttempt(storage);
            // completeSignIn removed code and state; the validated return path (or /) replaces the callback URL,
            // so a reload or a shared address can never replay the response.
            window.history.replaceState(window.history.state, '', appState.returnPath ?? '/');
            completedSignIn = true;
            answered = appState.reauthenticate;
            if (chosenId === undefined && appState.contextId !== undefined) {
                chosenId = appState.contextId;
                chosenOrganization = appState.organization;
            }
        }
        const returnPath = currentReturnPath();
        const scopedTo = checkAccess ? chosenId : undefined;
        // The workspace a sign-in from here is for: the one chosen in the gate (or carried through a sign-in).
        const chosenScope = {
            ...(scopedTo !== undefined ? { contextId: scopedTo } : {}),
            ...(scopedTo !== undefined && chosenOrganization ? { organization: chosenOrganization } : {}),
        };
        // Security relevant: the organization the hub handed over counts only on the way back from this tab's own
        // hub trip (the sign-in record says so), when the hub's redirect wrote it. Anywhere else the address is
        // anyone's to write (a link can carry any trace_organization), so it is never sent then.
        const hubTripScope = scopedTo === undefined && handedOrganization ? { organization: handedOrganization } : chosenScope;
        // b. A session for this application's own audience.
        let appToken = null;
        let scopeRefusal = null;
        try {
            if (session.mode === 'auth0' && !(await untilAborted(session.isAuthenticated(), signal))) {
                throw new SessionExpiredError('login_required');
            }
            live();
            appToken = await untilAborted(productToken(scopedTo, chosenOrganization), signal);
            live();
        }
        catch (error) {
            // An abandoned attempt ends here whatever the session answered: no marker, no sign-in below.
            live();
            // An Auth0 session signing in to the chosen workspace's organization: the page is leaving.
            if (isSignInRedirectError(error))
                return { kind: 'redirecting' };
            // No Auth0 organization for the chosen workspace: it cannot be carried in a token, fail closed.
            if (isContextNotCarriedError(error))
                throw new DestinationCheckError('context_not_carried', { cause: error });
            // A refused workspace means a session exists: it is answered below, like platform-api's answer would be.
            scopeRefusal = scopedTo === undefined ? null : scopeRefusalOf(error);
            if (scopeRefusal === null && !isSessionExpiredError(error))
                throw error;
        }
        if (appToken === null && scopeRefusal === null) {
            // A sign-in that just completed and still left no session must not start another one.
            if (completedSignIn)
                return failed('no_session', returnPath);
            const attempt = storage ? readSignInAttempt(storage, now()) : null;
            if (session.mode === 'auth0') {
                if (!storage) {
                    // This tab cannot remember a trip to the hub, so the hub step would repeat forever. The
                    // identity provider signs the person in directly instead; its callback completes it here. With no
                    // hub trip there is no handed organization to trust: only the workspace chosen here.
                    await session.beginSignIn({
                        productKey,
                        ...(returnPath ? { returnPath } : {}),
                        ...chosenScope,
                        ...withSignal,
                    });
                    return { kind: 'redirecting' };
                }
                // A browser that signed in here before skips the hub: the identity provider signs the person in
                // directly through its SSO session (prompt=none) with the remembered workspace. Only on a fresh attempt
                // (no marker), never for a workspace chosen here, and never twice: the marker records it.
                const last = !attempt && scopedTo === undefined && remembered ? readRememberedSignIn(remembered, now()) : null;
                if (last) {
                    writeSignInAttempt(storage, { stage: 'silent', at: now() });
                    try {
                        await session.beginSignIn({
                            productKey,
                            ...(returnPath ? { returnPath } : {}),
                            ...(last.organization ? { organization: last.organization } : {}),
                            prompt: 'none',
                            ...withSignal,
                        });
                    }
                    catch (error) {
                        clearSignInAttempt(storage);
                        throw error;
                    }
                    return { kind: 'redirecting' };
                }
                if (attempt?.stage === 'silent') {
                    // Back from a silent sign-in without a session and without an error: go through the hub.
                    clearSignInAttempt(storage);
                    return { kind: 'sign-in', hubUrl: signIn(returnPath) };
                }
                if (attempt?.stage === 'hub') {
                    const provider = { stage: 'provider', at: now() };
                    if (attempt.reauthenticate)
                        provider.reauthenticate = attempt.reauthenticate;
                    // Recorded before leaving: beginSignIn only returns when it could not start.
                    writeSignInAttempt(storage, provider);
                    try {
                        // The products require an Auth0 organization at sign-in (auth/tenant): the workspace chosen
                        // here, else the one the hub handed over with the way back.
                        await session.beginSignIn({
                            productKey,
                            ...(returnPath ? { returnPath } : {}),
                            ...hubTripScope,
                            ...(attempt.reauthenticate ? { reauthenticate: attempt.reauthenticate } : {}),
                            ...withSignal,
                        });
                    }
                    catch (error) {
                        // The sign-in did not start (it failed, or the attempt was abandoned before it could): the record
                        // goes back to the hub trip, so the next attempt answers it again.
                        writeSignInAttempt(storage, attempt);
                        throw error;
                    }
                    return { kind: 'redirecting' };
                }
                if (attempt?.stage === 'provider') {
                    clearSignInAttempt(storage);
                    return failed('no_session', returnPath);
                }
            }
            else if (attempt) {
                // Development identity: the hub's session is shared through the platform-api cookie, so being back
                // from the hub without one means it cannot work here (cookies blocked, platform-api restarted).
                clearSignInAttempt(storage);
                return failed('no_session', returnPath);
            }
            return { kind: 'sign-in', hubUrl: signIn(returnPath) };
        }
        // Signed in: the attempt is over. A reauthenticate requirement it answered is kept for the loop guard.
        if (storage) {
            answered ??= readSignInAttempt(storage, now())?.reauthenticate;
            clearSignInAttempt(storage);
        }
        const reauthenticate = (reason) => answered === reason
            ? failed('reauthenticate_loop', returnPath)
            : { kind: 'denied', denial: { kind: 'reauthenticate', reason, hubUrl: signIn(returnPath) } };
        const granted = (person, context, productGrant) => {
            // Remember this sign-in for the next tab (remembered.ts): only the organization the accepted token is in.
            if (remembered && session.mode === 'auth0') {
                const orgId = appToken ? decodeTokenPayload(appToken)?.org_id : undefined;
                writeRememberedSignIn(remembered, {
                    organization: isOrganizationReference(orgId) ? orgId : null,
                    at: now(),
                });
            }
            return grantedOutcome(person, context, productGrant);
        };
        const grantedOutcome = (person, context, productGrant) => ({
            kind: 'granted',
            access: {
                productKey,
                mode: session.mode,
                person,
                context,
                ...(productGrant ? { productGrant } : {}),
                returnPath,
            },
        });
        const accessDenied = (reason) => ({
            kind: 'denied',
            denial: {
                kind: 'access-denied',
                reason,
                hubUrl: hubUrl(hubOrigin, { route: 'access-denied', reason, productKey }),
            },
        });
        if (scopeRefusal === 'sso_required')
            return reauthenticate('sso_required');
        if (scopeRefusal === 'context_not_found')
            return accessDenied('context_not_found');
        if (appToken === null)
            throw new DestinationCheckError('unexpected_answer');
        // c. A product backend decides (checkAccess): only its product token is ever requested.
        if (checkAccess) {
            let token = appToken;
            let chosen = scopedTo;
            // Two rounds at most: the only workspace on offer is picked by itself once, and nothing loops.
            for (let round = 0; round < 2; round += 1) {
                let raw;
                try {
                    // The page host goes with every call: the host the backend names in its access check.
                    const hookOptions = {
                        host: window.location.host,
                        ...(chosen !== undefined ? { contextId: chosen } : {}),
                        ...withSignal,
                    };
                    raw = await untilAborted(checkAccess(token, hookOptions), signal);
                }
                catch (error) {
                    live();
                    if (isAbortError(error))
                        throw error;
                    if (statusOf(error) === 401)
                        return failed('session_rejected', returnPath);
                    throw error;
                }
                // Before anything below acts on the answer (a token for the only workspace on offer, for one).
                live();
                // The backend's answer is untrusted input: it must be a contract document before anything acts on it.
                const parsed = internalAccessCheckResponseSchema.safeParse(raw);
                if (!parsed.success)
                    throw new DestinationCheckError('unexpected_answer', { cause: parsed.error });
                const answer = parsed.data;
                if (answer.allowed) {
                    if (answer.productGrant.productKey !== productKey)
                        throw new DestinationCheckError('destination_mismatch');
                    if (chosen !== undefined && answer.context.id !== chosen)
                        throw new DestinationCheckError('context_mismatch');
                    // The context the token names, too: a backend must answer for exactly the workspace it was asked about.
                    if (!tokenNamesContext(token, answer.context, claimNamespace))
                        throw new DestinationCheckError('context_mismatch');
                    const entry = answer.context.products.find((p) => p.productKey === productKey);
                    if (entry?.state !== 'available')
                        throw new DestinationCheckError('unexpected_answer');
                    return granted(personOfUser(answer.user), answer.context, answer.productGrant);
                }
                switch (answer.reason) {
                    case 'context_required': {
                        // The person chose already, and the token still does not carry it: an error, never a loop.
                        if (chosen !== undefined)
                            throw new DestinationCheckError('context_not_carried');
                        const choices = (answer.choices ?? []).filter((c) => offersProduct(c, productKey));
                        if (choices.length === 0 || !answer.user)
                            throw new DestinationCheckError('no_workspace_to_choose');
                        const only = choices.length === 1 ? choices[0] : null;
                        const onlyEntry = only?.products.find((p) => p.productKey === productKey);
                        if (only && onlyEntry?.state === 'sso_required')
                            return reauthenticate('sso_required');
                        if (only && round === 0) {
                            chosen = only.id;
                            try {
                                token = await untilAborted(productToken(chosen, only.type === 'employee' ? only.organizationHint : undefined), signal);
                            }
                            catch (error) {
                                live();
                                if (isSignInRedirectError(error))
                                    return { kind: 'redirecting' };
                                if (isContextNotCarriedError(error)) {
                                    throw new DestinationCheckError('context_not_carried', { cause: error });
                                }
                                const refusal = scopeRefusalOf(error);
                                if (refusal === 'sso_required')
                                    return reauthenticate('sso_required');
                                if (refusal === 'context_not_found')
                                    return accessDenied('context_not_found');
                                throw error;
                            }
                            continue;
                        }
                        return { kind: 'choose-workspace', person: personOfUser(answer.user), contexts: choices };
                    }
                    case 'sso_required':
                        return reauthenticate('sso_required');
                    case 'session_revoked':
                        return failed('session_rejected', returnPath);
                    case 'product_unavailable':
                        return {
                            kind: 'denied',
                            denial: {
                                kind: 'unavailable',
                                hubUrl: hubUrl(hubOrigin, { route: 'unavailable', productKey }),
                            },
                        };
                    default:
                        return accessDenied(answer.reason);
                }
            }
            throw new DestinationCheckError('context_not_carried');
        }
        // c. Authorization, checked by platform-api.
        if (productKey === 'admin') {
            try {
                const me = await client.adminMe({ accessToken: appToken, ...(signal ? { signal } : {}) });
                return granted({ userId: me.userId, displayName: me.displayName }, {
                    id: 'staff',
                    type: 'staff',
                    staffRole: me.staffRole,
                    mfaSatisfied: me.mfaSatisfied,
                    permissions: me.permissions,
                });
            }
            catch (error) {
                const status = httpStatus(error);
                if (status === 403) {
                    if (isPlatformApiError(error) && error.problem.reason === 'mfa_required')
                        return reauthenticate('mfa_required');
                    // Not staff, or a refusal without a reason: denied, never guessed into access.
                    return {
                        kind: 'denied',
                        denial: {
                            kind: 'access-denied',
                            reason: 'staff_required',
                            hubUrl: hubUrl(hubOrigin, {
                                route: 'access-denied',
                                reason: 'staff_required',
                                productKey: 'admin',
                            }),
                        },
                    };
                }
                if (status === 401)
                    return failed('session_rejected', returnPath);
                throw error;
            }
        }
        let platformToken;
        try {
            // A destination without a backend may sign in for platform-api itself (the preview skeletons): its token is
            // then the platform token. Asking again for a second audience would fail without a refresh token for it
            // (auth0-spa-js refreshes only the audience it signed in for: missing_refresh_token, dev 2026-10-07).
            platformToken = audience === platformAudience ? appToken : await untilAborted(tokenFor(platformAudience), signal);
        }
        catch (error) {
            // The application token worked a moment ago, so this is a configuration problem, not a sign-in.
            if (isSessionExpiredError(error))
                throw new DestinationCheckError('platform_token_unavailable', { cause: error });
            throw error;
        }
        const requestOptions = { accessToken: platformToken, ...(signal ? { signal } : {}) };
        let decision;
        try {
            decision = await client.decide({ productKey, ...(chosenId ? { contextId: chosenId } : {}) }, requestOptions);
        }
        catch (error) {
            if (httpStatus(error) === 401)
                return failed('session_rejected', returnPath);
            throw error;
        }
        switch (decision.kind) {
            case 'redirect': {
                // The second, independent check: platform-api must send this product to this very origin.
                if (decision.productKey !== productKey || !isThisOrigin(decision.url)) {
                    throw new DestinationCheckError('destination_mismatch');
                }
                const me = await client.meAccess(requestOptions);
                const context = me.contexts.find((c) => c.id === decision.contextId);
                if (!context || context.type === 'staff')
                    throw new DestinationCheckError('context_missing');
                return granted(personOf(me), context);
            }
            case 'select-organization': {
                const me = await client.meAccess(requestOptions);
                const contexts = me.contexts.filter((c) => c.type !== 'staff' &&
                    c.products.some((p) => p.productKey === productKey && (p.state === 'available' || p.state === 'sso_required')));
                if (contexts.length === 0)
                    throw new DestinationCheckError('no_workspace_to_choose');
                return { kind: 'choose-workspace', person: personOf(me), contexts };
            }
            case 'select-product':
                // A request that names its product never needs a product chooser.
                throw new DestinationCheckError('unexpected_decision');
            case 'access-denied':
                return {
                    kind: 'denied',
                    denial: {
                        kind: 'access-denied',
                        reason: decision.reason,
                        hubUrl: hubUrl(hubOrigin, {
                            route: 'access-denied',
                            reason: decision.reason,
                            productKey: decision.productKey ?? productKey,
                        }),
                    },
                };
            case 'no-workspace':
                return {
                    kind: 'denied',
                    denial: { kind: 'no-workspace', hubUrl: hubUrl(hubOrigin, { route: 'no-workspace' }) },
                };
            case 'unavailable':
                return {
                    kind: 'denied',
                    denial: {
                        kind: 'unavailable',
                        hubUrl: hubUrl(hubOrigin, { route: 'unavailable', productKey: decision.productKey }),
                    },
                };
            case 'reauthenticate':
                return reauthenticate(decision.reason);
        }
    }
    catch (error) {
        // An abandoned attempt ends in its abort reason (an AbortError, or the gate's timed_out), never an outcome.
        if (signal?.aborted)
            throw signal.reason;
        if (isAbortError(error))
            throw error;
        return { kind: 'error', error };
    }
}
/**
 * Records, for this tab, that the person is going to the hub to sign in (or to answer a reauthenticate
 * requirement), so the destination recognizes the way back (see ensureDestinationAccess, step b). Call it
 * right before a navigation to a hub sign-in URL, for example from the click handler of a plain link.
 */
export function recordHubTrip(options = {}) {
    const storage = options.storage === undefined ? defaultAttemptStorage() : options.storage;
    if (!storage)
        return;
    const attempt = { stage: 'hub', at: (options.now ?? Date.now)() };
    if (options.reauthenticate)
        attempt.reauthenticate = options.reauthenticate;
    writeSignInAttempt(storage, attempt);
}
/**
 * Goes to the account hub to sign in (or to answer a reauthenticate requirement), recording the trip
 * first. Only for hub sign-in URLs built with hubUrl; other hub pages are plain navigations.
 */
export function leaveForHub(url, options = {}) {
    recordHubTrip(options);
    (options.assign ?? ((target) => window.location.assign(target)))(url);
}
//# sourceMappingURL=ensure-access.js.map